import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Amplify } from 'aws-amplify';
import {
	getCurrentUser,
	fetchAuthSession,
	signIn,
	signOut,
	signUp,
	confirmSignUp,
	resendSignUpCode,
	resetPassword,
	confirmResetPassword,
} from 'aws-amplify/auth';
import outputs from '../../amplify_outputs.json';
import { client } from '../utils/amplifyClient';
import { getStaffInfo } from '../graphql/queries';

Amplify.configure(outputs);
const existingConfig = Amplify.getConfig();
Amplify.configure({
	...existingConfig,
	Auth: {
		...existingConfig.Auth,
		Cognito: {
			...existingConfig.Auth?.Cognito,
			allowGuestAccess: true,
		},
	},
});

const initialState = {
	isAuthenticated: false,
	isInitialized: false,
	user: null,
	group: null,
};

const handlers = {
	INITIALIZE: (state, action) => {
		const { isAuthenticated, user, group } = action.payload;

		return {
			...state,
			isAuthenticated,
			isInitialized: true,
			user,
			group,
		};
	},
	LOGIN: (state, action) => {
		const { user, group } = action.payload;

		return {
			...state,
			isAuthenticated: true,
			user,
			group,
		};
	},
	LOGOUT: (state) => ({
		...state,
		isAuthenticated: false,
		user: null,
		group: null,
	}),
	REGISTER: (state) => ({ ...state }),
	VERIFY_CODE: (state) => ({ ...state }),
	RESEND_CODE: (state) => ({ ...state }),
	PASSWORD_RECOVERY: (state) => ({ ...state }),
	PASSWORD_RESET: (state) => ({ ...state }),
};

const reducer = (state, action) =>
	handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
	...initialState,
	platform: 'Amplify',
	login: () => Promise.resolve(),
	logout: () => Promise.resolve(),
	register: () => Promise.resolve(),
	verifyCode: () => Promise.resolve(),
	resendCode: () => Promise.resolve(),
	passwordRecovery: () => Promise.resolve(),
	passwordReset: () => Promise.resolve(),
});

const getGroup = async () => {
	try {
		const session = await fetchAuthSession();
		const group =
			session.tokens?.accessToken?.payload?.['cognito:groups']?.[0];
		return group ?? null;
	} catch (err) {
		return null;
	}
};

export const AuthProvider = (props) => {
	const { children } = props;
	const [state, dispatch] = useReducer(reducer, initialState);

	useEffect(() => {
		const initialize = async () => {
			try {
				const user = await getCurrentUser();
				const group = await getGroup();

				dispatch({
					type: 'INITIALIZE',
					payload: {
						isAuthenticated: true,
						user,
						group,
					},
				});
			} catch (error) {
				dispatch({
					type: 'INITIALIZE',
					payload: {
						isAuthenticated: false,
						user: null,
						group: null,
					},
				});
			}
		};

		initialize();
	}, []);

	const login = async (email, password) => {
		const result = await signIn({ username: email, password });

		if (!result.isSignedIn) {
			const step = result.nextStep?.signInStep;
			if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
				return result;
			}
			console.error(
				`Unable to login, sign-in step "${step}" is required.`
			);
			return result;
		}

		const user = await getCurrentUser();
		const group = await getGroup();

		dispatch({
			type: 'LOGIN',
			payload: {
				user,
				group,
			},
		});
		return result;
	};

	const updateContextGroup = async () => {
		const user = await getCurrentUser();
		const group = await getGroup();
		console.log('update context group');

		dispatch({
			type: 'LOGIN',
			payload: {
				group,
				user,
			},
		});
	};

	const logout = async () => {
		await signOut();
		dispatch({
			type: 'LOGOUT',
		});
	};

	const register = async (email, password) => {
		await signUp({
			username: email,
			password,
			options: { userAttributes: { email } },
		});
		dispatch({
			type: 'REGISTER',
		});
	};

	const verifyCode = async (username, code) => {
		await confirmSignUp({ username, confirmationCode: code });
		dispatch({
			type: 'VERIFY_CODE',
		});
	};

	const resendCode = async (username) => {
		await resendSignUpCode({ username });
		dispatch({
			type: 'RESEND_CODE',
		});
	};

	const passwordRecovery = async (username) => {
		await resetPassword({ username });
		dispatch({
			type: 'PASSWORD_RECOVERY',
		});
	};

	const passwordReset = async (username, code, newPassword) => {
		await confirmResetPassword({
			username,
			confirmationCode: code,
			newPassword,
		});
		dispatch({
			type: 'PASSWORD_RESET',
		});
	};

	// DBからFullNameを取得
	const getFullName = async (userName) => {
		const input = {
			id: userName
		};
		const res_gq = await client.graphql({
			query: getStaffInfo,
			variables: input,
		});
		const isDeleted = res_gq.data.getStaffInfo? res_gq.data.getStaffInfo.delete_flag: true;
		if (res_gq.data.getStaffInfo && !isDeleted) {
			return res_gq.data.getStaffInfo.fullname;
		} else {
			return '';
		}
	};

	return (
		<AuthContext.Provider
			value={{
				...state,
				platform: 'Amplify',
				login,
				logout,
				register,
				verifyCode,
				resendCode,
				passwordRecovery,
				passwordReset,
				updateContextGroup,
				getFullName
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

AuthProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default AuthContext;
